import React from "react";
import { TextField } from "../../../ui/components/TextField";
import { Button } from "../../../ui/components/Button";
import { isValidGstinFormat } from "../../utils/gstinUtils";

interface GstinVerificationFieldProps {
  gstin: string;
  gstTreatment: string;
  entityName: string;
  entityType: "vendor" | "customer";
  error?: string;
  disabled?: boolean;
  verificationStatus: "idle" | "verifying" | "verified" | "failed";
  verificationData: {
    legal_name?: string;
    trade_name?: string;
    status?: string;
    address?: string;
    state?: string;
    city?: string;
    pincode?: string;
  } | null;
  verificationRequired: boolean;
  nameMismatch: boolean;
  nameMatchConfirmed: boolean;
  errorMessage?: string;
  onGstinChange: (value: string) => void;
  onVerify: () => void;
  onConfirmNameMatch: () => void;
}

const GstinVerificationField: React.FC<GstinVerificationFieldProps> = ({
  gstin,
  gstTreatment,
  entityName,
  entityType,
  error,
  disabled,
  verificationStatus,
  verificationData,
  verificationRequired,
  nameMismatch,
  nameMatchConfirmed,
  errorMessage,
  onGstinChange,
  onVerify,
  onConfirmNameMatch,
}) => {
  const isBusinessGst = gstTreatment === "business_gst";
  const formatValid = isValidGstinFormat(gstin || "");
  const showVerifyButton =
    formatValid &&
    verificationStatus !== "verified" &&
    verificationStatus !== "verifying" &&
    verificationRequired &&
    isBusinessGst;

  const isActive = verificationData?.status === "Active";
  const hasStatus = verificationData?.status && verificationData.status.trim() !== "";
  const isInactive =
    verificationStatus === "verified" && verificationData && hasStatus && !isActive;
  const isUnverified =
    verificationStatus === "verified" && verificationData && !hasStatus;

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-end gap-2">
        <div className="flex-1">
          <TextField
            label={isBusinessGst ? "GSTIN*" : "GSTIN"}
            helpText={error || errorMessage}
            error={!!error || !!errorMessage}
            disabled={disabled || !isBusinessGst}
          >
            <TextField.Input
              placeholder="Enter GSTIN"
              maxLength={15}
              value={gstin || ""}
              onChange={(event) =>
                onGstinChange(
                  event.target.value.toUpperCase().replace(/[^0-9A-Z]/g, ""),
                )
              }
            />
          </TextField>
        </div>
        {showVerifyButton && (
          <Button
            variant="brand-primary"
            size="small"
            onClick={onVerify}
            style={{ marginBottom: error || errorMessage ? "24px" : "0" }}
          >
            Verify GSTIN
          </Button>
        )}
        {verificationStatus === "verifying" && (
          <div
            className="flex items-center gap-1 text-sm text-neutral-500"
            style={{ marginBottom: error || errorMessage ? "24px" : "0" }}
          >
            <svg
              className="animate-spin h-4 w-4"
              viewBox="0 0 24 24"
              fill="none"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
              />
            </svg>
            <span>Verifying...</span>
          </div>
        )}
      </div>

      {/* Verified - Active badge */}
      {verificationStatus === "verified" && isActive && (
        <div className="flex items-center gap-1 text-sm text-green-600 bg-green-50 px-2 py-1 rounded">
          <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clipRule="evenodd"
            />
          </svg>
          <span>
            Verified - Active | {verificationData?.legal_name}
            {verificationData?.trade_name
              ? ` (${verificationData.trade_name})`
              : ""}
          </span>
        </div>
      )}

      {/* Verified but inactive (Suspended/Cancelled) */}
      {isInactive && (
        <div className="flex items-center gap-1 text-sm text-red-600 bg-red-50 px-2 py-1 rounded">
          <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          <span>
            GSTIN is not active. Status: {verificationData?.status}
          </span>
        </div>
      )}

      {/* GSTIN not yet verified */}
      {isUnverified && verificationRequired && isBusinessGst && (
        <div className="flex items-center gap-1 text-sm text-amber-600 bg-amber-50 px-2 py-1 rounded">
          <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          <span>GSTIN is not verified. Please click "Verify GSTIN" to verify.</span>
        </div>
      )}

      {/* Verification failed */}
      {verificationStatus === "failed" && errorMessage && (
        <div className="flex items-center gap-1 text-sm text-red-600 bg-red-50 px-2 py-1 rounded">
          <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
              clipRule="evenodd"
            />
          </svg>
          <span>{errorMessage}</span>
        </div>
      )}

      {/* Verification disabled badge */}
      {!verificationRequired && isBusinessGst && formatValid && (
        <div className="flex items-center gap-1 text-sm text-neutral-500 bg-neutral-50 px-2 py-1 rounded">
          <span>GSTIN verification is disabled</span>
        </div>
      )}

      {/* Non-business_gst with GSTIN warning */}
      {!isBusinessGst && gstin && gstin.trim() !== "" && (
        <div className="text-sm text-red-600">
          GST Number should only be entered for Business GST entities in India.
        </div>
      )}
    </div>
  );
};

export default GstinVerificationField;
